import type { StoreDto } from '@cartly/shared';
import { Alert, Card } from '@cartly/ui';
import { CheckCircle2, Circle } from 'lucide-react';
import type { ConnectionInfo } from '../../api/types';
import { CopyField } from '../../components/Common';
import { formatRelative } from '../../app/format';
import { sdkFor } from './platforms';

function snippet(lang: ReturnType<typeof sdkFor>, info: ConnectionInfo): string {
  const base = info.apiBaseUrl.replace(/\/api\/public\/v1$/, '');
  switch (lang) {
    case 'php':
      return `composer require cartly/cartly-php

$cartly = new Cartly\\Client(getenv('CARTLY_API_KEY'), '${base}');
$cartly->sendEvent([
    'type' => 'order.created',
    'payload' => ['order' => [
        'externalId' => '1001',
        'currency' => 'INR',
        'total' => '499.00',
        'paymentMethod' => 'cod',
        'customer' => ['email' => 'asha@example.com', 'phone' => '+919876543210'],
    ]],
]);`;
    case 'python':
      return `pip install cartly

from cartly import Cartly
cartly = Cartly(api_key=os.environ["CARTLY_API_KEY"], base_url="${base}")
cartly.send_event({
    "type": "order.created",
    "payload": {"order": {
        "externalId": "1001", "currency": "INR", "total": "499.00", "paymentMethod": "cod",
        "customer": {"email": "asha@example.com", "phone": "+919876543210"},
    }},
})`;
    case 'node':
      return `npm install @cartly/sdk

import { Cartly } from '@cartly/sdk';
const cartly = new Cartly({ apiKey: process.env.CARTLY_API_KEY, baseUrl: '${base}' });
await cartly.sendEvent({
  type: 'order.created',
  payload: { order: { externalId: '1001', currency: 'INR', total: '499.00', paymentMethod: 'cod',
    customer: { email: 'asha@example.com', phone: '+919876543210' } } },
});`;
    default:
      return `curl -X POST ${info.apiBaseUrl}/events \\
  -H "Authorization: Bearer $CARTLY_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"type":"order.created","occurredAt":"2026-10-11T10:00:00Z",
       "payload":{"order":{"externalId":"1001","currency":"INR","total":"499.00","paymentMethod":"cod"}}}'`;
  }
}

const WOO_TOPICS = [
  'Order created',
  'Order updated',
  'Product created',
  'Product updated',
  'Product deleted',
  'Customer created',
  'Customer updated',
];

function Step({
  done,
  title,
  children,
}: {
  done: boolean;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <li className="setup-step">
      <span className={done ? 'setup-step__icon setup-step__icon--done' : 'setup-step__icon'}>
        {done ? <CheckCircle2 size={20} /> : <Circle size={20} />}
      </span>
      <div className="setup-step__body">
        <h3 className="setup-step__title">{title}</h3>
        {children}
      </div>
    </li>
  );
}

export function StoreSetup({
  store,
  info,
  hasSecretKey,
  onCreateKey,
}: {
  store: StoreDto;
  info: ConnectionInfo;
  hasSecretKey: boolean;
  onCreateKey: () => void;
}) {
  const connected = store.lastEventAt !== null;
  const lang = sdkFor(store.platform);

  return (
    <Card
      title="Connect this store"
      subtitle="Follow these steps once. Data starts flowing with the first event."
    >
      <ol className="setup-steps">
        <Step done={hasSecretKey} title="1. Create a secret API key">
          <p className="text-muted">
            The key authenticates your server; its signing secret verifies webhooks. Both are shown
            once.
          </p>
          {!hasSecretKey && (
            <button type="button" className="btn btn--primary btn--sm" onClick={onCreateKey}>
              Create secret key
            </button>
          )}
        </Step>

        {store.platform === 'woocommerce' ? (
          <Step done={connected} title="2. Add webhooks in WooCommerce">
            <p className="text-muted">
              In WordPress go to <strong>WooCommerce → Settings → Advanced → Webhooks</strong> and
              add one webhook per topic below. Use status <em>Active</em>, API version{' '}
              <em>WP REST API Integration v3</em>, and your key's <strong>signing secret</strong> as
              the webhook secret.
            </p>
            <CopyField label="Delivery URL" value={info.webhookUrls.woocommerce} />
            <div className="row">
              {WOO_TOPICS.map((t) => (
                <span key={t} className="badge">
                  {t}
                </span>
              ))}
            </div>
            <Alert tone="info">
              WooCommerce has no abandoned-cart webhook. Use the browser tracker or the PHP SDK to
              send cart and checkout events.
            </Alert>
          </Step>
        ) : (
          <Step done={connected} title="2. Send events from your backend">
            <p className="text-muted">
              Store the key in an environment variable and send events when things change.
            </p>
            <pre className="code-block">{snippet(lang, info)}</pre>
            <CopyField label="API base URL" value={info.apiBaseUrl} />
            <CopyField
              label="Signed webhook URL (alternative to the REST API)"
              value={info.webhookUrls.custom}
            />
          </Step>
        )}

        <Step done={connected} title="3. Confirm the connection">
          {connected ? (
            <p className="text-muted">
              Connected. Last event received {formatRelative(store.lastEventAt!)}.
            </p>
          ) : (
            <p className="text-muted">
              Waiting for the first event. This page updates when you reload it.
            </p>
          )}
          <CopyField label="Store ID" value={info.storeId} />
        </Step>
      </ol>
    </Card>
  );
}
